/**
 * Phase 27.50.C — Cart checkout page (`/checkout/cart`).
 * Phase 27.56 — Multi-payment (QuickBooks card + PayPal + Pay Later).
 *
 * Reuses:
 *   - `tokenizeCardWithIntuit` (SAQ-A-EP tokenizer — PAN goes direct to Intuit)
 *   - `useRecaptcha` (v3 fraud check with `action: cart_charge`)
 *   - `refreshIntent` pattern from MarketingCheckoutPage (retry-after-decline fix)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, Lock, ShieldCheck, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { shopAPI, shopPaymentsAPI } from '@/lib/api';
import { tokenizeCardWithIntuit } from '@/lib/intuitTokenizer';
import useRecaptcha from '@/hooks/useRecaptcha';
import { getErrorMessage } from '@/lib/utils';
import PaymentMethodPicker from '@/components/shop/PaymentMethodPicker';
import PayPalPayButton from '@/components/shop/PayPalPayButton';
import AfterpayPayButton from '@/components/shop/AfterpayPayButton';
import KlarnaPayButton from '@/components/shop/KlarnaPayButton';

const money = (n) => (n === null || n === undefined ? '—' : `$${Number(n).toFixed(2)}`);

export default function CartCheckoutPage() {
  const navigate = useNavigate();
  const [intent, setIntent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Phase 27.56 — multi-payment
  const [providers, setProviders] = useState([]);
  const [method, setMethod] = useState('card');
  const [cart, setCart] = useState(null);

  const numberRef = useRef();
  const expMonthRef = useRef();
  const expYearRef = useRef();
  const cvcRef = useRef();
  const [name, setName] = useState('');
  const [postal, setPostal] = useState('');

  const { ready: captchaReady, execute: executeCaptcha } = useRecaptcha(
    intent?.recaptcha_site_key,
  );

  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      // Providers list is the only *required* call — it drives the UI.
      // We fetch the QB intent in parallel but tolerate a failure so
      // customers can still pay with PayPal when QuickBooks is
      // temporarily unavailable.  The cart itself is the source of
      // truth for the order summary + total (server-side authoritative).
      const [provR, cartR, intentR] = await Promise.all([
        shopPaymentsAPI.listProviders(),
        shopAPI.getCart().catch(() => ({ data: null })),
        shopAPI.preparePayment().catch((e) => ({ __error: e })),
      ]);
      setCart(cartR.data || null);
      const provs = provR.data.providers || [];
      if (intentR && !intentR.__error) {
        setIntent(intentR.data);
        setProviders(provs);
      } else {
        setIntent(null);
        setProviders(provs.map((p) => (p.id === 'quickbooks' ? { ...p, enabled: false } : p)));
        const pp = provs.find((p) => p.id === 'paypal' && p.enabled);
        if (pp) setMethod('paypal');
      }
    } catch (e) {
      setLoadError(getErrorMessage(e, 'Could not initialize checkout'));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const paypalConfig = (providers.find((p) => p.id === 'paypal') || {}).public_config;
  const paypalEnabled = !!(providers.find((p) => p.id === 'paypal') || {}).enabled;
  const afterpayEnabled = !!(providers.find((p) => p.id === 'afterpay') || {}).enabled;
  const klarnaEnabled = !!(providers.find((p) => p.id === 'klarna') || {}).enabled;

  const onProviderApproved = (label) => async (data) => {
    if (data?.payment_status === 'paid') {
      setResult({
        order_id: data.order_id,
        outcome: 'CAPTURED',
        charge_id: data.provider_payment_id,
        brand: label,
      });
      toast.success(`Payment received via ${label}`);
    } else if (data?.payment_status === 'processing') {
      toast.info(`${label} is still processing — we will email you the receipt shortly.`);
    } else {
      toast.error(`${label} returned an unexpected status`);
    }
  };

  const onPayPalApproved = onProviderApproved('PayPal');
  const onKlarnaApproved = onProviderApproved('Klarna');

  const refreshIntent = async () => {
    try {
      const r = await shopAPI.preparePayment();
      setIntent(r.data);
    } catch (e) {
      setLoadError(getErrorMessage(e, 'Could not refresh — reload the page'));
    }
  };

  const submit = async () => {
    if (!intent) return;
    if (!captchaReady) return toast.error('Fraud check still loading — try again in a second');

    // Client-side minimums.
    const number = numberRef.current?.value?.replace(/\s+/g, '');
    const expMonth = expMonthRef.current?.value;
    const expYear = expYearRef.current?.value;
    const cvc = cvcRef.current?.value;
    if (!number || !expMonth || !expYear || !cvc || !name || !postal) {
      return toast.error('Fill all card and billing fields');
    }

    setSubmitting(true);
    try {
      // 1. Tokenize card directly with Intuit (never touches our server).
      const tok = await tokenizeCardWithIntuit({
        tokenizationUrl: intent.tokenization_url,
        card: { number, expMonth, expYear, cvc, name, postal },
      });
      // 2. reCAPTCHA v3.
      const recaptchaToken = await executeCaptcha('cart_charge');
      // 3. Charge on our backend.
      const r = await shopAPI.chargeCart({
        intent_id: intent.intent_id,
        payment_token: tok.value,
        recaptcha_token: recaptchaToken,
        snapshot_hash: intent.snapshot_hash,
      });
      const d = r.data || {};
      if (d.outcome === 'CAPTURED') {
        setResult({
          order_id: d.order_id,
          brand: tok.card.brand, last4: tok.card.last4,
          charge_id: d.charge_id,
          sales_receipt_doc_number: d.sales_receipt_doc_number,
        });
        toast.success('Payment captured — thank you!');
      } else {
        toast.error(d.error
          ? `Payment declined: ${d.error}. Try another card.`
          : 'Payment declined by the issuer. Try another card.');
        await refreshIntent();
      }
    } catch (err) {
      const code = err?.response?.status;
      const msg = err?.response?.data?.detail || err?.message || 'Payment failed';
      toast.error(msg);
      if (code === 404 || code === 409) await refreshIntent();
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="max-w-3xl mx-auto p-8 flex items-center gap-2 text-slate-500">
      <Loader2 size={16} className="animate-spin" /> Loading checkout…
    </div>
  );
  if (loadError) return (
    <div className="max-w-3xl mx-auto p-8" data-testid="cart-checkout-error">
      <Card>
        <CardContent className="p-6 text-rose-700 bg-rose-50 border border-rose-200 rounded">
          {loadError}
          <div className="mt-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/cart')}>
              <ArrowLeft size={13} className="mr-1.5" /> Back to cart
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
  if (result) return (
    <div className="max-w-lg mx-auto p-8 text-center" data-testid="cart-checkout-success">
      <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
        <CheckCircle2 className="text-emerald-600" size={32} />
      </div>
      <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment received</h1>
      <p className="text-slate-500 text-sm mb-6">
        We&apos;ve emailed you a receipt. Your order <span className="font-mono">{result.order_id}</span> is being processed.
      </p>
      <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-left text-sm space-y-1 mb-6">
        <div><strong>Charge:</strong> <span className="font-mono">{result.charge_id}</span></div>
        {result.sales_receipt_doc_number && (
          <div><strong>Receipt #:</strong> {result.sales_receipt_doc_number}</div>
        )}
        <div><strong>Card:</strong> {result.brand} •••• {result.last4}</div>
      </div>
      <div className="flex gap-2 justify-center">
        <Button variant="outline" onClick={() => navigate('/shop')}>Keep shopping</Button>
        <Button onClick={() => navigate('/my-orders')} data-testid="cart-checkout-view-orders">
          View my orders
        </Button>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="cart-checkout-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/cart')} className="mb-4">
        <ArrowLeft size={13} className="mr-1.5" /> Back to cart
      </Button>
      <h1 className="text-2xl font-bold text-slate-900 mb-1">Secure Checkout</h1>
      <p className="text-sm text-slate-500 mb-6 flex items-center gap-1">
        <Lock size={12} /> Card data goes directly to Intuit — never touches KR servers.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Payment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">

            <PaymentMethodPicker
              providers={providers}
              value={method}
              onChange={setMethod}
            />

            {method === 'card' && (
              <div className="space-y-3 pt-2 border-t">
                <div>
                  <Label className="text-xs">Card number *</Label>
                  <Input placeholder="4111 1111 1111 1111" ref={numberRef}
                         inputMode="numeric" autoComplete="cc-number"
                         data-testid="cc-number" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Exp month *</Label>
                    <Input placeholder="12" maxLength={2} ref={expMonthRef}
                           inputMode="numeric" autoComplete="cc-exp-month"
                           data-testid="cc-exp-month" />
                  </div>
                  <div>
                    <Label className="text-xs">Exp year *</Label>
                    <Input placeholder="2029" maxLength={4} ref={expYearRef}
                           inputMode="numeric" autoComplete="cc-exp-year"
                           data-testid="cc-exp-year" />
                  </div>
                  <div>
                    <Label className="text-xs">CVV *</Label>
                    <Input placeholder="123" maxLength={4} ref={cvcRef}
                           inputMode="numeric" autoComplete="cc-csc"
                           data-testid="cc-cvc" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Name on card *</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)}
                         autoComplete="cc-name" data-testid="cc-name" />
                </div>
                <div>
                  <Label className="text-xs">Billing ZIP *</Label>
                  <Input value={postal} onChange={(e) => setPostal(e.target.value)}
                         autoComplete="postal-code" data-testid="cc-postal" />
                </div>

                <div className="pt-3 flex items-center justify-between border-t">
                  <span className="text-[11px] text-slate-400 flex items-center gap-1">
                    <ShieldCheck size={11} /> reCAPTCHA v3 · PCI SAQ-A-EP
                  </span>
                  <Button onClick={submit} disabled={submitting || !captchaReady || !intent}
                          data-testid="cart-checkout-pay-btn">
                    {submitting && <Loader2 size={13} className="mr-1.5 animate-spin" />}
                    Pay {money(intent?.amount ?? cart?.total)}
                  </Button>
                </div>
                {!intent && (
                  <p className="text-[11px] text-rose-600 text-center">
                    Credit card is temporarily unavailable — please choose PayPal above.
                  </p>
                )}
              </div>
            )}

            {(method === 'paypal' || method === 'paypal_paylater') && paypalEnabled && (
              <div className="pt-2 border-t space-y-2" data-testid="paypal-section">
                <p className="text-xs text-slate-500">
                  You'll be redirected to PayPal to confirm this payment.
                </p>
                <PayPalPayButton
                  paypalConfig={paypalConfig}
                  method={method}
                  onApproved={onPayPalApproved}
                  onError={(e) => toast.error(getErrorMessage(e, 'PayPal payment failed'))}
                />
                <div className="mt-3 flex items-start gap-2 rounded-md bg-blue-50 border border-blue-100 p-2.5" data-testid="paypal-buyer-protection">
                  <ShieldCheck size={14} className="text-blue-700 shrink-0 mt-0.5" />
                  <div className="text-[11px] leading-snug text-blue-900">
                    <span className="font-semibold">PayPal Buyer Protection.</span>{' '}
                    Eligible transactions are covered by PayPal for the full purchase price plus original shipping.{' '}
                    <a href="https://www.paypal.com/us/legalhub/buyer-protection" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-700">
                      Learn more
                    </a>
                  </div>
                </div>
              </div>
            )}

            {method === 'afterpay' && afterpayEnabled && (
              <div className="pt-2 border-t space-y-2" data-testid="afterpay-block">
                <p className="text-xs text-slate-500">
                  Split into 4 interest-free payments with Afterpay.
                </p>
                <AfterpayPayButton />
              </div>
            )}

            {method === 'klarna' && klarnaEnabled && (
              <div className="pt-2 border-t space-y-2" data-testid="klarna-block">
                <p className="text-xs text-slate-500">
                  Pay now, in 4, or in 30 days — choose at Klarna.
                </p>
                <KlarnaPayButton onApproved={onKlarnaApproved} />
              </div>
            )}

            <p className="text-[11px] text-slate-400 text-center pt-1">
              By paying you agree to our{' '}
              <Link to="/terms" target="_blank" className="underline hover:text-slate-600" data-testid="checkout-terms-link">Terms</Link>,{' '}
              <Link to="/privacy" target="_blank" className="underline hover:text-slate-600" data-testid="checkout-privacy-link">Privacy Policy</Link>, and{' '}
              <Link to="/refund-policy" target="_blank" className="underline hover:text-slate-600" data-testid="checkout-refund-link">Refund &amp; Return Policy</Link>.
            </p>
          </CardContent>
        </Card>

        <Card className="lg:col-span-1 h-fit">
          <CardHeader>
            <CardTitle className="text-base">Order Summary</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            {((intent?.line_items) || (cart?.items || []).map((i) => ({
              template_id: i.template_id, title: i.title,
              qty: i.quantity, unit_price: i.unit_price,
            }))).map((i, idx) => (
              <div key={i.template_id || idx} className="flex justify-between text-slate-700">
                <span className="truncate mr-2">{i.title} × {i.qty}</span>
                <span className="font-mono">{money(i.unit_price * i.qty)}</span>
              </div>
            ))}
            <div className="border-t pt-2 flex justify-between text-slate-500 text-xs">
              <span>Subtotal</span>
              <span className="font-mono">{money(intent?.subtotal ?? cart?.subtotal)}</span>
            </div>
            {Number((intent?.discount_amount ?? cart?.discount_amount) || 0) > 0 && (
              <div className="flex justify-between text-emerald-700 text-xs">
                <span>Discount {(intent?.coupon_code ?? cart?.coupon_code) ? `(${intent?.coupon_code ?? cart?.coupon_code})` : ''}</span>
                <span className="font-mono">-{money(intent?.discount_amount ?? cart?.discount_amount)}</span>
              </div>
            )}
            <div className="flex justify-between text-slate-500 text-xs">
              <span>Shipping</span>
              <span className="font-mono">{money(intent?.shipping_amount ?? cart?.shipping_amount)}</span>
            </div>
            <div className="border-t pt-2 flex justify-between font-bold text-lg text-slate-900">
              <span>Total</span>
              <span className="font-mono" data-testid="cart-checkout-total">
                {money(intent?.amount ?? cart?.total)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
